/**
 * `users/{uid}.notificationPreferences` — the ten per-category switches.
 *
 * The authority is `functions/notifications/prefs.js`, which is what actually
 * gates a send; `mobile/src/lib/firebase/queries/notificationPreferences.ts`
 * is the third copy. The three runtimes share no code, so they are kept in
 * step by hand and a drift shows up as a switch that does nothing.
 *
 * WEB REGISTERS NO PUSH TOKEN. There is no getMessaging, no service worker
 * and no VAPID key in this repo. This page therefore configures what the
 * user's PHONE receives, and the UI says so.
 *
 * NOT A SECURITY BOUNDARY: Firestore rules are world-open.
 */
export type NotificationCategoryId =
  | 'habits' | 'competitions' | 'groups' | 'appointments'
  | 'courses' | 'points' | 'cases' | 'exams' | 'clinic' | 'questionnaire';

export const NOTIFICATION_CATEGORY_IDS: readonly NotificationCategoryId[] = [
  'habits', 'competitions', 'groups', 'appointments',
  'courses', 'points', 'cases', 'exams', 'clinic', 'questionnaire',
];

export type NotificationPreferences = Partial<Record<NotificationCategoryId, boolean>>;

/** Absent means enabled. Only a literal `false` silences a category. */
export function isCategoryEnabled(
  prefs: NotificationPreferences | undefined,
  category: NotificationCategoryId
): boolean {
  return prefs?.[category] !== false;
}

/**
 * The `setDoc(..., { merge: true })` payload for ONE category. Firestore
 * deep-merges nested maps key by key, so this never clobbers a sibling and
 * never writes an all-ten payload built from stale local state.
 */
export function categoryPatch(
  category: NotificationCategoryId,
  enabled: boolean
): { notificationPreferences: NotificationPreferences } {
  return { notificationPreferences: { [category]: enabled } };
}
